import { Assignment, ConfirmationNumber, CheckCircle, Warning, Info } from '@mui/icons-material';
import { Box, Typography, Button, Stack, Chip } from '@mui/material';
import { format } from 'date-fns';
import { useState } from 'react';

import type { TicketInterface } from 'types';

interface TicketCardProps {
    ticket: TicketInterface;
    resolvingIds: Set<number>;
    onToggleResolve: (ticket: TicketInterface, isResolved: boolean) => void;
}

const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('resolved') || statusLower.includes('approved')) {
        return 'success';
    }
    if (statusLower.includes('pending') || statusLower.includes('open')) {
        return 'warning';
    }
    if (statusLower.includes('rejected') || statusLower.includes('failed')) {
        return 'error';
    }
    return 'info';
};

const getStatusIcon = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('resolved') || statusLower.includes('approved')) {
        return <CheckCircle fontSize="small" />;
    }
    if (statusLower.includes('pending') || statusLower.includes('open')) {
        return <Warning fontSize="small" />;
    }
    if (statusLower.includes('rejected') || statusLower.includes('failed')) {
        return <Info fontSize="small" />;
    }
    return <Assignment fontSize="small" />;
};

const TicketCard = ({ ticket, resolvingIds, onToggleResolve }: TicketCardProps) => {
    const [expanded, setExpanded] = useState(false);
    const TRUNCATE_LEN = 170;
    const shouldTruncate = ticket.text && ticket.text.length > TRUNCATE_LEN;
    const truncatedText = shouldTruncate
        ? `${ticket.text.slice(0, TRUNCATE_LEN).trimEnd()}…`
        : ticket.text;

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.paper',
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                border: '2px dashed',
                borderColor: `${getStatusColor(ticket.status)}.main`,
                '&:hover': {
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    transform: 'translateY(-2px)',
                },
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: -10,
                    top: '65%',
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    bgcolor: 'background.default',
                    boxShadow: 'inset 0 0 0 1px',
                    boxShadowColor: `${getStatusColor(ticket.status)}.main`,
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    right: -10,
                    top: '65%',
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    bgcolor: 'background.default',
                    boxShadow: 'inset 0 0 0 1px',
                    boxShadowColor: `${getStatusColor(ticket.status)}.main`,
                },
            }}
        >
            {/* Main Content Area */}
            <Box
                sx={{
                    flex: 1,
                    p: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    borderBottom: '2px dashed',
                    borderBottomColor: `${getStatusColor(ticket.status)}.light`,
                    position: 'relative',
                }}
            >
                {/* Top Section */}
                <Stack direction="row" sx={{ flex: 1, justifyContent: 'space-between' }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5 }}>
                        <ConfirmationNumber
                            sx={{
                                display: { xs: 'none', sm: 'block' },
                                fontSize: '1.25rem',
                                color: `${getStatusColor(ticket.status)}.main`,
                            }}
                        />
                        <Typography variant="subtitle2">{ticket.document_name}</Typography>
                    </Stack>
                    <Chip
                        size="small"
                        variant="outlined"
                        icon={getStatusIcon(ticket.status)}
                        label={ticket.status}
                        color={getStatusColor(ticket.status)}
                    />
                </Stack>

                {/* Message */}

                <Typography
                    variant="body2"
                    onClick={() => setExpanded((prev) => !prev)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setExpanded((prev) => !prev);
                        }
                    }}
                    role={shouldTruncate ? 'button' : undefined}
                    tabIndex={shouldTruncate ? 0 : -1}
                    aria-expanded={shouldTruncate ? expanded : undefined}
                    sx={{
                        color: 'text.primary',
                        lineHeight: 1.6,
                        fontStyle: 'italic',
                        flex: 1,
                        py: 1,
                        px: 2,
                        bgcolor: 'action.hover',
                        borderRadius: 1,
                        borderLeft: '3px solid',
                        borderLeftColor: `${getStatusColor(ticket.status)}.main`,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        cursor: shouldTruncate ? 'pointer' : 'default',
                        minHeight: 0,
                    }}
                >
                    {expanded || !shouldTruncate ? ticket.text : truncatedText}
                </Typography>
            </Box>

            {/* Ticket Stub/Action Section */}
            <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', p: 1.5 }}>
                <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: 1,
                            fontWeight: 600,
                        }}
                    >
                        #{ticket.id}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'text.secondary',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            fontWeight: 500,
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                bgcolor: 'text.secondary',
                            }}
                        />
                        Issued: {format(new Date(ticket.created), 'MMM d, yyyy')}
                    </Typography>
                </Stack>

                <Button
                    size="small"
                    disabled={resolvingIds.has(ticket.id)}
                    onClick={() =>
                        onToggleResolve(ticket, ticket.status.toLowerCase().includes('resolved'))
                    }
                    variant="contained"
                    color={getStatusColor(ticket.status)}
                    sx={{
                        textTransform: 'none',
                        borderRadius: 2,
                        fontSize: '0.875rem',
                    }}
                >
                    {resolvingIds.has(ticket.id)
                        ? 'Processing...'
                        : ticket.status.toLowerCase().includes('resolved')
                          ? '↻ Reopen'
                          : '✓ Resolve'}
                </Button>
            </Stack>
        </Box>
    );
};

export default TicketCard;
